import { FC, useId, useState } from "react";

import { Stack } from "@mui/material";
import {
  AI_SESSION_FEEDBACK_REASON_MAX_RATING,
  AI_SESSION_FEEDBACK_REASONS,
  AiSessionFeedbackReason,
} from "@simplyk/common";

import { RatingCard } from "./RatingCard/RatingCard";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardBaseInput } from "@/dashboard-design-system/BaseInput/DashboardBaseInput";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChipFilterItem } from "@/dashboard-design-system/DashboardChipFilter";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

const COMMENT_MAX_LENGTH = 1000;

const REASON_LABEL_KEYS = {
  wrong_information: "aiElements.feedback.reason.wrongInformation",
  did_not_understand: "aiElements.feedback.reason.didNotUnderstand",
  cannot_do_what_i_need: "aiElements.feedback.reason.cannotDoWhatINeed",
  wanted_a_person: "aiElements.feedback.reason.wantedAPerson",
  too_slow: "aiElements.feedback.reason.tooSlow",
} as const satisfies Record<AiSessionFeedbackReason, string>;

const scrollIntoViewOnOpen = (form: HTMLFormElement | null) =>
  form?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });

export interface AssistantFeedbackProps {
  question: string;
  rating: number | null;
  comment: string | null;
  reason?: AiSessionFeedbackReason | null;
  isSaving: boolean;
  hasFailed: boolean;
  onRate: (rating: number) => void;
  onSubmitComment: (comment: string) => void;
  onSelectReason?: (reason: AiSessionFeedbackReason) => void;
  onDone?: () => void;
  draft?: string;
  onDraftChange?: (draft: string) => void;
}

export const AssistantFeedback: FC<AssistantFeedbackProps> = ({
  question,
  rating,
  comment,
  reason = null,
  isSaving,
  hasFailed,
  onRate,
  onSubmitComment,
  onSelectReason,
  onDone,
  draft: controlledDraft,
  onDraftChange,
}) => {
  const { t } = useTranslate();
  const questionId = useId();
  const reasonQuestionId = useId();
  const [ownDraft, setOwnDraft] = useState("");
  const draft = controlledDraft ?? ownDraft;
  const setDraft = onDraftChange ?? setOwnDraft;
  const [isCommentSkipped, setIsCommentSkipped] = useState(false);
  const [isChangingRating, setIsChangingRating] = useState(false);
  const ratings = [
    { value: 1, emoji: "😡", label: t("dashboard", "aiElements.feedback.rating.terrible") },
    { value: 2, emoji: "🤒", label: t("dashboard", "aiElements.feedback.rating.bad") },
    { value: 3, emoji: "😐", label: t("dashboard", "aiElements.feedback.rating.okay") },
    { value: 4, emoji: "☺️", label: t("dashboard", "aiElements.feedback.rating.great") },
    { value: 5, emoji: "🤩", label: t("dashboard", "aiElements.feedback.rating.excellent") },
  ];
  const selectedRating = ratings.find(({ value }) => value === rating);
  const recap = selectedRating && !isChangingRating ? selectedRating : null;
  const asksReason = rating !== null && rating <= AI_SESSION_FEEDBACK_REASON_MAX_RATING && Boolean(onSelectReason);
  const rate = (value: number) => {
    setIsChangingRating(false);
    onRate(value);
  };
  return (
    <Stack spacing={2} role="group" aria-labelledby={questionId} data-test="assistant-feedback" sx={{ width: "100%" }}>
      {recap === null ? (
        <>
          <DashboardTypography
            id={questionId}
            variant="h6"
            component="h2"
            vibe="text-neutral-intense"
            sx={{ px: 2, textAlign: "center" }}
          >
            {question}
          </DashboardTypography>
          <Stack direction="row" spacing={0.5}>
            {ratings.map(({ value, emoji, label }) => (
              <RatingCard
                key={value}
                emoji={emoji}
                label={label}
                isSelected={rating === value}
                data-test={`assistant-feedback-rating-${value}`}
                onSelect={() => rate(value)}
              />
            ))}
          </Stack>
        </>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          data-test="assistant-feedback-recap"
        >
          <DashboardTypography id={questionId} variant="body2" vibe="text-neutral-moderate">
            <span aria-hidden>{recap.emoji} </span>
            {t("dashboard", "aiElements.feedback.ratedAs", { rating: recap.label })}
          </DashboardTypography>
          <DashboardButton variant="text" size="small" onClick={() => setIsChangingRating(true)}>
            {t("dashboard", "aiElements.feedback.changeRating")}
          </DashboardButton>
        </Stack>
      )}
      {asksReason && (
        <Stack spacing={1} role="group" aria-labelledby={reasonQuestionId}>
          <DashboardTypography
            id={reasonQuestionId}
            variant="h6"
            component="h2"
            vibe="text-neutral-intense"
            sx={{ textAlign: "center" }}
          >
            {t("dashboard", "aiElements.feedback.reason.question")}
          </DashboardTypography>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
            {AI_SESSION_FEEDBACK_REASONS.map((value) => (
              <DashboardChipFilterItem
                key={value}
                value={value}
                selected={reason === value}
                label={t("dashboard", REASON_LABEL_KEYS[value])}
                data-test={`assistant-feedback-reason-${value}`}
                onChange={() => reason !== value && onSelectReason?.(value)}
              />
            ))}
          </Stack>
        </Stack>
      )}
      {rating !== null && comment && (
        <DashboardTypography variant="body2" role="status" sx={{ textAlign: "center" }}>
          {t("dashboard", "aiElements.feedback.commentThanks")}
        </DashboardTypography>
      )}
      {rating !== null && !comment && !isCommentSkipped && (
        <Stack
          component="form"
          spacing={2}
          alignItems="center"
          ref={scrollIntoViewOnOpen}
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim()) {
              onSubmitComment(draft.trim());
            } else {
              setIsCommentSkipped(true);
            }
            onDone?.();
          }}
        >
          <Stack spacing={1} sx={{ width: "100%", pt: 1 }}>
            <DashboardTypography
              variant="subtitle1"
              vibe="text-neutral-moderate"
              role="status"
              sx={{ textAlign: "center" }}
            >
              {t("dashboard", rating === 4 ? "aiElements.feedback.missingForFive" : "aiElements.feedback.thanks")}
            </DashboardTypography>
            <DashboardBaseInput
              multiline
              minRows={3}
              maxRows={8}
              value={draft}
              placeholder={t("dashboard", "aiElements.feedback.commentPlaceholder")}
              onChange={(event) => setDraft(event.target.value)}
              inputProps={{
                "aria-label": t("dashboard", "aiElements.feedback.commentPlaceholder"),
                "data-test": "assistant-feedback-comment",
                maxLength: COMMENT_MAX_LENGTH,
              }}
            />
          </Stack>
          <DashboardButton type="submit" size="large" variant="filled" disabled={isSaving}>
            {t("dashboard", "aiElements.feedback.submit")}
          </DashboardButton>
        </Stack>
      )}
      {hasFailed && (
        <DashboardTypography role="alert" variant="body2" vibe="text-danger-intense">
          {t("dashboard", "aiElements.feedback.error")}
        </DashboardTypography>
      )}
    </Stack>
  );
};
